import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useResource, formatDateTime, titleCase } from "../lib/hooks";
import { EntityForm } from "../components/EntityForm";
import { ManagedListsCard } from "../components/ManagedListsCard";
import { Feature, FEATURE_LABELS, useEntitlements } from "../lib/entitlements";
import { NegotiatedRatesCard } from "../components/NegotiatedRatesCard";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Modal } from "../components/Modal";
import { uploadFile } from "../lib/files";
import { useBranding, invalidateBranding } from "../lib/branding";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { QualityPermissionsPanel } from "../components/QualityPermissionsPanel";
import { PermissionMatrix } from "../components/PermissionMatrix";

interface UserRow {
    id: string;
    email: string;
    full_name: string;
    role: string;
    employee_id: string | null;
    is_active: boolean;
    last_login_at: string | null;
    can_access_shiftflow: boolean;
    can_access_crm: boolean;
}

interface ResetLink {
    userId: string;
    fullName: string;
    email: string;
    token: string;
    expires_at: string;
}

interface AccessRequestRow {
    id: string;
    user_id: string;
    surface: "crm" | "shiftflow";
    note: string | null;
    status: string;
    created_at: string;
}

/**
 * Permission groups for the tick matrix.
 *
 * A group header checkbox ticks or clears its whole group; individual boxes
 * refine it. The keys match PERMISSION_KEYS on the API — an unknown key is
 * rejected there, so the two lists cannot silently diverge.
 */
const PERMISSION_GROUPS: Array<{ heading: string; items: Array<{ key: string; label: string }> }> = [
    {
        heading: "ShiftFlow — Operate",
        items: [
            { key: "shiftflow.dashboard", label: "Dashboard" },
            { key: "shiftflow.rostering", label: "Rostering" },
            { key: "shiftflow.time_clock", label: "Time Clock" },
            { key: "shiftflow.site_tasks", label: "Site tasks" },
        ],
    },
    {
        heading: "ShiftFlow — People",
        items: [
            { key: "shiftflow.employees", label: "Employees" },
            { key: "shiftflow.training", label: "Training & compliance" },
            { key: "shiftflow.leave", label: "Availability & leave" },
        ],
    },
    {
        heading: "ShiftFlow — Service & governance",
        items: [
            { key: "shiftflow.participants", label: "Participants" },
            { key: "shiftflow.incidents", label: "Incidents" },
            { key: "shiftflow.policies", label: "Policies" },
            { key: "shiftflow.updates", label: "Updates" },
        ],
    },
    {
        heading: "CRM",
        items: [
            { key: "crm.dashboard", label: "Dashboard" },
            { key: "crm.pipeline", label: "Pipeline" },
            { key: "crm.companies", label: "Companies" },
            { key: "crm.contacts", label: "Contacts" },
            { key: "crm.inquiries", label: "Inquiries" },
            { key: "crm.quotes", label: "Quotes" },
            { key: "crm.referrals", label: "Referrals" },
            { key: "crm.analytics", label: "Analytics" },
            { key: "crm.staff", label: "Staff" },
            { key: "crm.compliance", label: "Compliance" },
            { key: "crm.partner_requests", label: "Partner requests" },
        ],
    },
    {
        heading: "CRM — Money",
        items: [
            { key: "crm.invoices", label: "Invoices" },
            { key: "crm.timesheets", label: "Timesheets" },
            { key: "crm.finance", label: "Finance" },
        ],
    },
];

const ALL_KEYS = PERMISSION_GROUPS.flatMap((g) => g.items.map((i) => i.key));

/**
 * One-click permission bundles (C10 phase 4). A preset REPLACES the current
 * ticks with a sensible starting set — the admin can refine before saving.
 * The Finance preset is the blueprint's ask: a bookkeeper who sees money and
 * nothing clinical.
 */
const PERMISSION_PRESETS: Array<{ label: string; help: string; keys: string[] }> = [
    {
        label: "Finance officer",
        help: "Money modules only: dashboard, invoices, timesheets, finance. No clinical or workforce access.",
        keys: ["crm.dashboard", "crm.invoices", "crm.timesheets", "crm.finance"],
    },
    {
        label: "Intake coordinator",
        help: "Referrals, partner requests, participants and rostering visibility — the intake desk.",
        keys: ["crm.dashboard", "crm.referrals", "crm.partner_requests", "crm.companies", "crm.contacts",
            "shiftflow.dashboard", "shiftflow.participants", "shiftflow.rostering"],
    },
    {
        label: "Field staff",
        help: "The working day: dashboard, rostering view, time clock, participants, leave.",
        keys: ["shiftflow.dashboard", "shiftflow.time_clock", "shiftflow.participants",
            "shiftflow.leave", "shiftflow.training"],
    },
];

export const Settings = () => {
    const users = useResource<UserRow[]>("/auth/users");
    const partnerCompanies = useResource<Array<{ id: string; name: string }>>("/companies");
    const employees = useResource<Array<{ id: string; full_name: string }>>("/employees");
    const requests = useResource<AccessRequestRow[]>("/access-requests?status=requested");

    const [selected, setSelected] = useState<UserRow | null>(null);
    const [permissions, setPermissions] = useState<string[]>([]);
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [dialog, setDialog] = useState<"create" | { decline: AccessRequestRow } | null>(null);
    const [resetLink, setResetLink] = useState<ResetLink | null>(null);
    const [accountBusyId, setAccountBusyId] = useState<string | null>(null);
    const [section, setSection] = useState<"branding" | "plan" | "lists" | "rates" | "integrations" | "users" | "matrix">("branding");

    const userById = useMemo(
        () => new Map((users.data ?? []).map((u) => [u.id, u])),
        [users.data]
    );

    useEffect(() => {
        if (!selected) return;
        setPermissionsLoaded(false);
        api.get<string[]>(`/access/users/${selected.id}/permissions`)
            .then((keys) => { setPermissions(keys); setPermissionsLoaded(true); })
            .catch(() => setPermissionsLoaded(true));
    }, [selected?.id]);

    const toggle = (key: string) =>
        setPermissions((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));

    const toggleGroup = (keys: string[]) =>
        setPermissions((p) => {
            const allOn = keys.every((k) => p.includes(k));
            return allOn ? p.filter((k) => !keys.includes(k)) : [...new Set([...p, ...keys])];
        });

    const savePermissions = async () => {
        if (!selected) return;
        setSaving(true);
        setMessage(null);
        try {
            await api.put(`/access/users/${selected.id}/permissions`, { permissions });
            setMessage("Permissions saved. They apply on the user's next page load.");
        } catch (err) {
            setMessage(err instanceof Error ? err.message : "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const [linkMessage, setLinkMessage] = useState<string | null>(null);

    /**
     * The account-to-employee link is what makes My pay and My profile real
     * for the person behind the login — admins included. The API refuses a
     * link to an employee another active account already holds.
     */
    const linkEmployee = async (user: UserRow, employeeId: string) => {
        setLinkMessage(null);
        try {
            await api.put(`/auth/users/${user.id}/employee`, { employeeId: employeeId || null });
        } catch (err) {
            setLinkMessage(err instanceof Error ? err.message : "Could not update the link");
        }
        users.reload();
    };

    const saveSurfaces = async (user: UserRow, shiftflow: boolean, crm: boolean) => {
        try {
            await api.put(`/access/users/${user.id}/surfaces`, {
                canAccessShiftflow: shiftflow,
                canAccessCrm: crm,
            });
            users.reload();
        } catch {
            users.reload();
        }
    };

    /**
     * Tightest possible reset: the API emails the one-time link straight to
     * the account's REGISTERED address from the system sender. The link never
     * reaches this page — nobody, admin included, ever sees it. A dead link
     * is fixed by clicking Reset again (a new link invalidates the old one).
     */
    const resetPassword = async (user: UserRow) => {
        setAccountBusyId(user.id);
        try {
            const sent = await api.post<{ sent: boolean; email: string; expires_at: string }>(
                `/auth/users/${user.id}/reset-password`, {});
            setResetLink({ userId: user.id, fullName: user.full_name, email: sent.email,
                token: "", expires_at: sent.expires_at });
        } catch (err) {
            setLinkMessage(err instanceof Error ? err.message : "Could not send the reset link");
        } finally {
            setAccountBusyId(null);
        }
    };

    const setAccountActive = async (user: UserRow, active: boolean) => {
        setAccountBusyId(user.id);
        try {
            await api.put(`/auth/users/${user.id}/active`, { active });
            users.reload();
        } catch (err) {
            setLinkMessage(err instanceof Error ? err.message : "Could not update the account");
        } finally {
            setAccountBusyId(null);
        }
    };

    const decideRequest = async (request: AccessRequestRow, verdict: "approve" | "decline") => {
        await api.post(`/access/requests/${request.id}/${verdict}`, {});
        requests.reload();
        users.reload();
    };

    if (users.error) return <div className="error-banner">{users.error}</div>;

    return (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "1.1rem" }}>
            <div className="tabs" style={{ marginBottom: 0 }}>
                {([
                    ["branding", "Branding"],
                    ["plan", "Plan & features"],
                    ["lists", "Lists & dropdowns"],
                    ["rates", "Negotiated rates"],
                    ["integrations", "Integrations"],
                    ["users", "Users & access"],
                    ["matrix", "Permission matrix"],
                ] as const).map(([value, label]) => (
                    <button key={value} className={section === value ? "active" : ""} onClick={() => setSection(value)}>
                        {label}
                        {value === "users" && (requests.data ?? []).length > 0 && (
                            <span className="chip warn" style={{ marginLeft: 6 }}>{(requests.data ?? []).length}</span>
                        )}
                    </button>
                ))}
            </div>
            {section === "branding" && <BrandingCard />}
            {section === "plan" && <PlanFeaturesCard />}
            {section === "lists" && <ManagedListsCard />}
            {section === "rates" && <NegotiatedRatesCard />}
            {section === "integrations" && <IntegrationsCard />}
            {section === "matrix" && <PermissionMatrix />}
            {section === "users" && (
                <InvitesCard companies={partnerCompanies.data ?? []} onAccepted={() => users.reload()}
                    onManual={() => setDialog("create")} />
            )}
            {dialog === "create" && (
                <EntityForm
                    title="Create account" path="/auth/users"
                    subtitle="There is no self-signup. Accounts exist because an admin made them."
                    fields={[
                        { name: "email", label: "Email", kind: "email", required: true, half: true },
                        { name: "fullName", label: "Full name", kind: "text", required: true, half: true },
                        { name: "password", label: "Temporary password", kind: "text", required: true,
                          help: "At least 12 characters. Ask them to change it after first sign-in." },
                        { name: "role", label: "Role", kind: "select", required: true, options: [
                            { value: "staff", label: "Staff" },
                            { value: "supervisor", label: "Supervisor" },
                            { value: "manager", label: "Manager" },
                            { value: "viewer", label: "Viewer (CRM read-only)" },
                            { value: "partner", label: "Partner (external organisation)" },
                            { value: "support", label: "Support (IT helpdesk — accounts only)" },
                            { value: "admin", label: "Admin" },
                        ] },
                        { name: "employeeId", label: "Employee record (optional)", kind: "select",
                          options: (employees.data ?? []).map((e) => ({ value: e.id, label: e.full_name })),
                          help: "Link the account to the person's employee record so My pay and My profile work for them." },
                        { name: "companyId", label: "Organisation (partner accounts)", kind: "select",
                          options: (partnerCompanies.data ?? []).map((c) => ({ value: c.id, label: c.name })),
                          help: "Required for partner accounts — it decides which clients they can see." },
                    ]}
                    onClose={() => setDialog(null)}
                    onSaved={() => { setDialog(null); users.reload(); }}
                />
            )}
            {dialog && dialog !== "create" && (
                <ConfirmDialog
                    title="Decline this access request?"
                    message={`${userById.get(dialog.decline.user_id)?.full_name ?? "The user"} will keep their current access.`}
                    confirmLabel="Decline"
                    tone="danger"
                    request={{ method: "post", path: `/access/requests/${dialog.decline.id}/decline` }}
                    onClose={() => setDialog(null)}
                    onDone={() => { setDialog(null); requests.reload(); }}
                />
            )}

            {section === "users" && (requests.data ?? []).length > 0 && (
                <div className="card">
                    <div className="card-head">
                        <h2>Access requests</h2>
                        <span className="chip warn">{(requests.data ?? []).length} waiting</span>
                    </div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr><th>User</th><th>Wants</th><th>Note</th><th>Asked</th><th style={{ textAlign: "right" }}>Decision</th></tr>
                                </thead>
                                <tbody>
                                    {(requests.data ?? []).map((request) => (
                                        <tr key={request.id}>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                                    <InitialsAvatar name={userById.get(request.user_id)?.full_name} size={24} />
                                                    {userById.get(request.user_id)?.full_name ?? request.user_id}
                                                </div>
                                            </td>
                                            <td><span className="chip info">{request.surface === "crm" ? "CRM" : "ShiftFlow"}</span></td>
                                            <td style={{ color: "var(--ink-soft)" }}>{request.note ?? "—"}</td>
                                            <td className="mono">{formatDateTime(request.created_at)}</td>
                                            <td>
                                                <div className="row-actions">
                                                    <button className="icon-btn" onClick={() => void decideRequest(request, "approve")}>Approve</button>
                                                    <button className="icon-btn danger" onClick={() => setDialog({ decline: request })}>Decline</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {section === "users" && linkMessage && <div className="error-banner">{linkMessage}</div>}

            {section === "users" && resetLink && (
                <div className="card">
                    <div className="card-head">
                        <div>
                            <h2>Reset link emailed — {resetLink.fullName}</h2>
                            <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                                Sent automatically to their registered address <b>{resetLink.email}</b>.
                                It works once and expires {formatDateTime(resetLink.expires_at)}. The link is
                                never shown to anyone — you included. If it expires or doesn't arrive,
                                click Reset password again to send a fresh one (the old link stops working).
                            </p>
                        </div>
                        <button className="btn ghost small" onClick={() => setResetLink(null)}>Close</button>
                    </div>
                </div>
            )}

            {section === "users" && <div className="card">
                <div className="card-head">
                    <h2>Accounts & surface access</h2>
                    <button className="btn small" onClick={() => setDialog("create")}>+ Create account</button>
                </div>
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>User</th><th>Role</th><th>Employee record</th>
                                    <th>ShiftFlow</th><th>CRM</th><th>Status</th>
                                    <th>Last sign-in</th><th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(users.data ?? []).map((user) => (
                                    <tr key={user.id}>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                                <InitialsAvatar name={user.full_name} size={30} />
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>{user.full_name}</div>
                                                    <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{user.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td><span className="chip info">{titleCase(user.role)}</span></td>
                                        <td>
                                            {user.role === "partner" ? (
                                                <span style={{ fontSize: "0.75rem", color: "var(--muted)" }}>n/a — organisation</span>
                                            ) : (
                                                <select
                                                    value={user.employee_id ?? ""}
                                                    aria-label={`Employee record for ${user.full_name}`}
                                                    onChange={(e) => void linkEmployee(user, e.target.value)}
                                                >
                                                    <option value="">— not linked —</option>
                                                    {(employees.data ?? []).map((emp) => (
                                                        <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </td>
                                        <td>
                                            <label className="check">
                                                <input
                                                    type="checkbox"
                                                    checked={user.can_access_shiftflow}
                                                    onChange={(e) => void saveSurfaces(user, e.target.checked, user.can_access_crm)}
                                                />
                                                <span className="chip mute">access</span>
                                            </label>
                                        </td>
                                        <td>
                                            <label className="check">
                                                <input
                                                    type="checkbox"
                                                    checked={user.can_access_crm}
                                                    onChange={(e) => void saveSurfaces(user, user.can_access_shiftflow, e.target.checked)}
                                                />
                                                <span className="chip mute">access</span>
                                            </label>
                                        </td>
                                        <td>
                                            {user.is_active
                                                ? <span className="chip pass">Active</span>
                                                : <span className="chip block">Deactivated</span>}
                                        </td>
                                        <td className="mono">
                                            {user.last_login_at ? formatDateTime(user.last_login_at) : "never"}
                                        </td>
                                        <td>
                                            <div className="row-actions">
                                                <button className="icon-btn" onClick={() => setSelected(user)}>
                                                    Edit permissions
                                                </button>
                                                {user.is_active ? (
                                                    <>
                                                        <button className="icon-btn" disabled={accountBusyId === user.id}
                                                            onClick={() => void resetPassword(user)}>
                                                            Reset password
                                                        </button>
                                                        <button className="icon-btn danger" disabled={accountBusyId === user.id}
                                                            onClick={() => void setAccountActive(user, false)}>
                                                            Deactivate
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button className="icon-btn" disabled={accountBusyId === user.id}
                                                        onClick={() => void setAccountActive(user, true)}>
                                                        Reactivate
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>}

            {section === "users" && selected && (
                <div className="card">
                    <div className="card-head">
                        <div>
                            <h2>Module permissions — {selected.full_name}</h2>
                            <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                                No boxes ticked means the role's defaults apply. Tick any box and the ticks
                                become the whole truth for that user — an unticked module disappears from
                                their menu. For most modules that hides the page; for Invoices, Timesheets
                                and Finance it also blocks the underlying actions (generate, approve, mark
                                paid, and so on) even if someone calls the API directly — those three are
                                enforced, not just hidden.
                            </p>
                        </div>
                        <button className="btn ghost small" onClick={() => setSelected(null)}>Close</button>
                    </div>
                    <div className="card-body">
                        {!permissionsLoaded ? (
                            <p style={{ color: "var(--muted)" }}>Loading…</p>
                        ) : (
                            <>
                                <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap", marginBottom: "0.8rem" }}>
                                    <span style={{ fontSize: "0.72rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                        Presets
                                    </span>
                                    {PERMISSION_PRESETS.map((preset) => (
                                        <button key={preset.label} className="chip-toggle" type="button"
                                            title={preset.help}
                                            onClick={() => setPermissions([...preset.keys])}>
                                            {preset.label}
                                        </button>
                                    ))}
                                    <span style={{ fontSize: "0.72rem", color: "var(--muted)" }}>
                                        — applies the ticks below; refine, then Save.
                                    </span>
                                </div>
                                {PERMISSION_GROUPS.map((group) => {
                                    const keys = group.items.map((i) => i.key);
                                    const onCount = keys.filter((k) => permissions.includes(k)).length;
                                    return (
                                        <div key={group.heading} className="perm-group">
                                            <label className="perm-group-head">
                                                <input
                                                    type="checkbox"
                                                    checked={onCount === keys.length}
                                                    ref={(el) => {
                                                        // Indeterminate is the honest state for a
                                                        // part-ticked group; HTML can only express
                                                        // it from script.
                                                        if (el) el.indeterminate = onCount > 0 && onCount < keys.length;
                                                    }}
                                                    onChange={() => toggleGroup(keys)}
                                                />
                                                {group.heading}
                                                <span className="chip mute">{onCount}/{keys.length}</span>
                                            </label>
                                            <div className="perm-items">
                                                {group.items.map((item) => (
                                                    <label key={item.key}>
                                                        <input
                                                            type="checkbox"
                                                            checked={permissions.includes(item.key)}
                                                            onChange={() => toggle(item.key)}
                                                        />
                                                        {item.label}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}

                                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.75rem" }}>
                                    <button className="btn" onClick={savePermissions} disabled={saving}>
                                        {saving ? "Saving…" : "Save permissions"}
                                    </button>
                                    <button className="btn ghost" onClick={() => setPermissions([])}>
                                        Clear all (revert to role defaults)
                                    </button>
                                    <button className="btn ghost" onClick={() => setPermissions([...ALL_KEYS])}>
                                        Tick everything
                                    </button>
                                    {message && <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{message}</span>}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {section === "users" && selected && (
                <QualityPermissionsPanel userId={selected.id} userName={selected.full_name} />
            )}
        </div>
    );
};


/**
 * White-label identity. A buyer running this instance puts their own name,
 * tagline and logo here; it appears on the sign-in page and the sidebar.
 *
 * What this card cannot do — deliberately — is remove the "Powered by
 * TrustCare" mark. It is rendered unconditionally by the shell with no flag
 * behind it, so provenance survives a resale.
 */
/**
 * PS0: the commercial knob. Five feature flags + the plan label. Flipping a
 * flag is immediate (server cache invalidates); everything ships off.
 */
const PlanFeaturesCard = () => {
    const entitlements = useEntitlements();
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const toggleFeature = async (feature: Feature, enabled: boolean) => {
        setBusy(feature);
        setError(null);
        try {
            await api.put(`/entitlements/${feature}`, { enabled });
            entitlements.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not update the feature");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="card">
            <div className="card-head">
                <div>
                    <h2>Plan &amp; features</h2>
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                        Premium features are off until enabled here. The server enforces every switch —
                        turning one off locks its pages and its API immediately. Current plan:{" "}
                        <span className="chip info">{entitlements.plan}</span>
                    </p>
                </div>
            </div>
            <div className="card-body" style={{ display: "grid", gap: "0.55rem" }}>
                {error && <div className="error-banner">{error}</div>}
                {(Object.keys(FEATURE_LABELS) as Feature[]).map((feature) => {
                    const meta = FEATURE_LABELS[feature];
                    const on = entitlements.enabled(feature);
                    return (
                        <div key={feature} className="file-row">
                            <span style={{ minWidth: 110, fontWeight: 700 }}>
                                {meta.name}
                                {meta.metered && <span className="chip warn" style={{ marginLeft: 6 }}>metered</span>}
                            </span>
                            <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{meta.blurb}</span>
                            <label className="check" style={{ whiteSpace: "nowrap" }}>
                                <input type="checkbox" checked={on} disabled={busy === feature}
                                    onChange={(e) => void toggleFeature(feature, e.target.checked)} />
                                {on ? "Enabled" : "Off"}
                            </label>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const BrandingCard = () => {
    const branding = useBranding();
    const [orgName, setOrgName] = useState(branding.orgName);
    const [tagline, setTagline] = useState(branding.tagline ?? "");
    const [payInstructions, setPayInstructions] = useState("");
    const [support, setSupport] = useState({ phone: "", email: "", hours: "", safety: "" });
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string | null>(null);

    useEffect(() => {
        setOrgName(branding.orgName);
        setTagline(branding.tagline ?? "");
        setSupport({
            phone: branding.supportPhone ?? "",
            email: branding.supportEmail ?? "",
            hours: branding.supportHours ?? "",
            safety: branding.safetyContact ?? "",
        });
    }, [branding]);

    const save = async (logoFileId?: string | null) => {
        setBusy(true);
        setMessage(null);
        try {
            await api.put("/branding", {
                orgName: orgName.trim() || "TrustCare Support",
                tagline: tagline.trim() || null,
                supportPhone: support.phone.trim() || null,
                supportEmail: support.email.trim() || null,
                supportHours: support.hours.trim() || null,
                safetyContact: support.safety.trim() || null,
                ...(payInstructions.trim() ? { paymentInstructions: payInstructions.trim() } : {}),
                ...(logoFileId !== undefined ? { logoFileId } : {}),
            });
            invalidateBranding();
            setMessage("Saved. Reload the page to see it everywhere.");
        } catch (err) {
            setMessage(err instanceof Error ? err.message : "Could not save");
        } finally {
            setBusy(false);
        }
    };

    const uploadLogo = async (file: File) => {
        setBusy(true);
        setMessage(null);
        try {
            const stored = await uploadFile(file, "general");
            await save(stored.id);
        } catch (err) {
            setMessage(err instanceof Error ? err.message : "Upload failed");
            setBusy(false);
        }
    };

    return (
        <div className="card">
            <div className="card-head">
                <div>
                    <h2>Organisation branding</h2>
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                        Shown on the sign-in page and the sidebar. The "Powered by TrustCare" mark is
                        part of the product and stays regardless of what is set here.
                    </p>
                </div>
            </div>
            <div className="card-body">
                <div className="form-grid">
                    <div className="field half">
                        <label htmlFor="org-name">Organisation name</label>
                        <input id="org-name" value={orgName} onChange={(e) => setOrgName(e.target.value)} />
                    </div>
                    <div className="field half">
                        <label htmlFor="org-tagline">Tagline (sign-in page)</label>
                        <input id="org-tagline" value={tagline} onChange={(e) => setTagline(e.target.value)}
                               placeholder="CRM & ShiftFlow" />
                    </div>
                    <div className="field half">
                        <label htmlFor="org-logo">Logo (PNG or SVG, square works best, max 10 MB)</label>
                        <input id="org-logo" type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp"
                               disabled={busy}
                               onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadLogo(f); }} />
                    </div>
                    <div className="field half">
                        <label>Current logo</label>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                            <img src={branding.logoUrl ?? "/brand/icon.png"} alt="" height={34}
                                 style={{ borderRadius: 6, background: "var(--surface-sunk)", padding: 3 }} />
                            {branding.hasLogo && (
                                <button className="icon-btn" disabled={busy} onClick={() => void save(null)}>
                                    Remove custom logo
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <div className="form-grid" style={{ marginTop: "0.5rem" }}>
                    <div className="field half">
                        <label htmlFor="org-support-phone">Support phone</label>
                        <input id="org-support-phone" value={support.phone}
                            onChange={(e) => setSupport((s) => ({ ...s, phone: e.target.value }))}
                            placeholder="08 0000 0000" />
                    </div>
                    <div className="field half">
                        <label htmlFor="org-support-email">Support email</label>
                        <input id="org-support-email" type="email" value={support.email}
                            onChange={(e) => setSupport((s) => ({ ...s, email: e.target.value }))}
                            placeholder="support@yourorganisation.com.au" />
                    </div>
                    <div className="field half">
                        <label htmlFor="org-support-hours">Support hours</label>
                        <input id="org-support-hours" value={support.hours}
                            onChange={(e) => setSupport((s) => ({ ...s, hours: e.target.value }))}
                            placeholder="Monday to Friday, 8:30am – 4:30pm (except public holidays)" />
                    </div>
                    <div className="field half">
                        <label htmlFor="org-safety">Safety concerns contact</label>
                        <input id="org-safety" value={support.safety}
                            onChange={(e) => setSupport((s) => ({ ...s, safety: e.target.value }))}
                            placeholder="safety@yourorganisation.com.au" />
                        <span className="help">Shown separately so a safeguarding concern never queues behind IT support.</span>
                    </div>
                </div>
                <div className="field" style={{ marginTop: "0.5rem" }}>
                    <label htmlFor="org-pay">Payment instructions (shown to partners on the invoice Pay screen)</label>
                    <textarea id="org-pay" value={payInstructions}
                        onChange={(e) => setPayInstructions(e.target.value)}
                        placeholder={"e.g.\nBank transfer to TrustCare Support Pty Ltd\nBSB 000-000  Account 0000 0000\nReference: the invoice number"} />
                </div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.5rem" }}>
                    <button className="btn" onClick={() => void save()} disabled={busy}>
                        {busy ? "Saving..." : "Save branding"}
                    </button>
                    {message && <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{message}</span>}
                </div>
            </div>
        </div>
    );
};


interface InviteRow {
    id: string;
    email: string;
    full_name: string;
    role: string;
    expires_at: string;
    expired: boolean;
}

/** Delivery facts only — the API emails the link to the invitee and never
 *  returns it, exactly like the password-reset flow. */
interface NewInviteLink {
    fullName: string;
    email: string;
    expires_at: string;
}

interface ImportResultRow {
    line: number;
    email: string;
    ok: boolean;
    error?: string;
    sent?: boolean;
}

/**
 * Invite onboarding — the safer path. The person sets their own password from
 * a single-use, expiring link, so no temporary password ever exists to be
 * intercepted or forwarded. Manual creation below still works for edge cases;
 * this should be the default.
 */
const InvitesCard = ({ companies, onAccepted, onManual }: {
    companies: Array<{ id: string; name: string }>;
    onAccepted: () => void;
    onManual: () => void;
}) => {
    const [choosing, setChoosing] = useState(false);
    const invites = useResource<InviteRow[]>("/invites");
    const [creating, setCreating] = useState(false);
    const [importing, setImporting] = useState(false);
    const [csv, setCsv] = useState("");
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [newLink, setNewLink] = useState<NewInviteLink | null>(null);
    const [importResults, setImportResults] = useState<ImportResultRow[] | null>(null);

    const runImport = async () => {
        setBusy(true);
        setMessage(null);
        try {
            const result = await api.post<{ created: number; results: ImportResultRow[] }>(
                "/invites/import", { csv });
            setMessage(`${result.created} invite(s) created — each person has been emailed their personal link.`);
            setImportResults(result.results);
            setCsv("");
            setImporting(false);
            invites.reload();
        } catch (err) {
            setMessage(err instanceof Error ? err.message : "Import failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="card">
            <div className="card-head">
                <div>
                    <h2>Invite users</h2>
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                        The personal link is emailed straight to them — they set their own password.
                        Single use, expires in 7 days, and never shown to anyone here.
                    </p>
                </div>
                <button className="btn" onClick={() => setChoosing(true)}>+ Add users</button>
            </div>

            {newLink && (
                <div className="card-body" style={{ borderBottom: "1px solid var(--line-soft)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                        <div>
                            <strong>Invite emailed — {newLink.fullName}</strong>
                            <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                                Sent automatically to <b>{newLink.email}</b>. The link works once, expires
                                {" "}{formatDateTime(newLink.expires_at)}, and is never shown to anyone — you
                                included. If it doesn't arrive, invite them again (the old link stops working).
                            </p>
                        </div>
                        <button className="btn ghost small" onClick={() => setNewLink(null)}>Close</button>
                    </div>
                </div>
            )}

            {choosing && (
                <Modal title="Add users" subtitle="Three ways in — pick what fits." onClose={() => setChoosing(false)}>
                    <div className="add-users-options">
                        <button className="add-users-option" onClick={() => { setChoosing(false); setCreating(true); }}>
                            <strong>Invite by link</strong>
                            <span>Send a personal single-use link — they set their own password. The safest default.</span>
                        </button>
                        <button className="add-users-option" onClick={() => { setChoosing(false); setImporting(true); }}>
                            <strong>Import from file</strong>
                            <span>Paste CSV rows (email, full name, role) and create invites in bulk.</span>
                        </button>
                        <button className="add-users-option" onClick={() => { setChoosing(false); onManual(); }}>
                            <strong>Add manually</strong>
                            <span>Create the account yourself with a temporary password. For edge cases only.</span>
                        </button>
                    </div>
                </Modal>
            )}

            {importing && (
                <div className="card-body" style={{ borderBottom: "1px solid var(--line-soft)" }}>
                    <div className="field">
                        <label htmlFor="inv-csv">One person per line: email, full name, role</label>
                        <textarea id="inv-csv" value={csv} onChange={(e) => setCsv(e.target.value)}
                            placeholder={"jane@example.com, Jane Poole, staff\nraj@example.com, Raj Patel, supervisor"}
                            style={{ minHeight: 110, fontFamily: "var(--mono)", fontSize: "0.8rem" }} />
                        <span className="help">Roles: staff, supervisor, manager, viewer, admin. Partner invites need the single form (they must name the organisation).</span>
                    </div>
                    <button className="btn" disabled={busy || !csv.trim()} onClick={() => void runImport()}>
                        {busy ? "Importing…" : "Create invites"}
                    </button>
                </div>
            )}

            {message && (
                <div className="card-body" style={{ borderBottom: "1px solid var(--line-soft)" }}>
                    <p style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{message}</p>
                    {importResults && importResults.length > 0 && (
                        <div className="table-wrap" style={{ marginTop: "0.4rem" }}>
                            <table>
                                <tbody>
                                    {importResults.map((row) => (
                                        <tr key={row.line}>
                                            <td style={{ fontSize: "0.8rem" }}>{row.email || `line ${row.line}`}</td>
                                            <td>
                                                {row.ok
                                                    ? <span className="chip pass">Invite emailed ✓</span>
                                                    : <span className="chip block">{row.error}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <div className="card-body flush">
                <div className="table-wrap">
                    <table>
                        <thead><tr><th>Invited</th><th>Role</th><th>Expires</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
                        <tbody>
                            {(invites.data ?? []).map((invite) => (
                                <tr key={invite.id}>
                                    <td>
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                            <InitialsAvatar name={invite.full_name} size={24} />
                                            {invite.full_name}
                                        </div>
                                        <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{invite.email}</div>
                                    </td>
                                    <td><span className="chip info">{invite.role}</span></td>
                                    <td>
                                        {invite.expired
                                            ? <span className="chip block">expired</span>
                                            : <span className="mono" style={{ fontSize: "0.78rem" }}>{formatDateTime(invite.expires_at)}</span>}
                                    </td>
                                    <td>
                                        <div className="row-actions">
                                            <button className="icon-btn danger" onClick={async () => {
                                                await api.delete(`/invites/${invite.id}`);
                                                invites.reload();
                                            }}>Revoke</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {(invites.data ?? []).length === 0 && (
                                <tr><td colSpan={4} className="empty">No pending invites.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {creating && (
                <EntityForm
                    title="Invite user" path="/invites"
                    subtitle="They receive a personal link and choose their own password."
                    fields={[
                        { name: "email", label: "Email", kind: "email", required: true, half: true },
                        { name: "fullName", label: "Full name", kind: "text", required: true, half: true },
                        { name: "role", label: "Role", kind: "select", required: true, options: [
                            { value: "staff", label: "Staff" },
                            { value: "supervisor", label: "Supervisor" },
                            { value: "manager", label: "Manager" },
                            { value: "viewer", label: "Viewer (CRM read-only)" },
                            { value: "partner", label: "Partner (external organisation)" },
                            { value: "support", label: "Support (IT helpdesk — accounts only)" },
                            { value: "admin", label: "Admin" },
                        ], half: true },
                        { name: "companyId", label: "Organisation (partner invites)", kind: "select", half: true,
                          options: companies.map((c) => ({ value: c.id, label: c.name })) },
                        { name: "accountKind", label: "Account kind", kind: "select", half: true, options: [
                            { value: "member", label: "Member (permanent)" },
                            { value: "auditor", label: "Auditor (read-only, time-boxed — viewer role)" },
                        ] },
                        { name: "accessEndsAt", label: "Access ends (auditors, last day, max 90 days)",
                          kind: "date", half: true },
                    ]}
                    onClose={() => setCreating(false)}
                    onSaved={(saved) => {
                        setCreating(false);
                        const created = saved as { full_name: string; email: string; sent?: boolean; expires_at: string } | undefined;
                        if (created?.sent) {
                            setNewLink({
                                fullName: created.full_name, email: created.email,
                                expires_at: created.expires_at,
                            });
                        }
                        invites.reload();
                        onAccepted();
                    }}
                />
            )}
        </div>
    );
};


interface IntegrationRow {
    provider: string;
    label: string;
    configured: boolean;
    status: "connected" | "disconnected" | "error";
    tenantName: string | null;
    connectedAt: string | null;
    instructions: string | null;
}

const INTEGRATION_COLOURS: Record<string, string> = {
    xero: "#13B5EA", quickbooks: "#2CA01C", myob: "#6100A5",
};

interface SyncLogRow {
    provider: string;
    entityLabel: string | null;
    outcome: string;
    detail: string | null;
    createdAt: string;
}

/**
 * The accounting integrations (migration 088). Connect opens the PROVIDER's
 * own sign-in in a popup - the CRM never sees the password; the provider
 * calls back with a code and the connection lands server-side. A provider
 * without app credentials says exactly what an admin must register instead
 * of pretending, and the poll picks the connection up the moment the popup
 * finishes.
 */
const IntegrationsCard = () => {
    const integrations = useResource<IntegrationRow[]>("/integrations");
    const syncLog = useResource<SyncLogRow[]>("/integrations/sync-log");
    const [error, setError] = useState<string | null>(null);
    const [showSetup, setShowSetup] = useState<IntegrationRow | null>(null);
    const [waiting, setWaiting] = useState<string | null>(null);
    const [pushing, setPushing] = useState<string | null>(null);
    const [pushResult, setPushResult] = useState<string | null>(null);

    useEffect(() => {
        if (!waiting) return;
        // While the popup is open, watch for the connection to land.
        const timer = window.setInterval(() => integrations.reload(), 3000);
        const stop = window.setTimeout(() => setWaiting(null), 120_000);
        return () => { window.clearInterval(timer); window.clearTimeout(stop); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waiting]);

    useEffect(() => {
        const row = (integrations.data ?? []).find((r) => r.provider === waiting);
        if (row?.status === "connected") setWaiting(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [integrations.data]);

    const connect = async (row: IntegrationRow) => {
        setError(null);
        if (!row.configured) {
            setShowSetup(row);
            return;
        }
        try {
            const begun = await api.post<{ authorizeUrl: string }>(
                `/integrations/${row.provider}/connect`, {});
            window.open(begun.authorizeUrl, `connect-${row.provider}`,
                "width=560,height=720,noopener");
            setWaiting(row.provider);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not start the connection");
        }
    };

    const disconnect = async (row: IntegrationRow) => {
        setError(null);
        try {
            await api.post(`/integrations/${row.provider}/disconnect`, {});
            integrations.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not disconnect");
        }
    };

    // The sync stage: copy issued invoices into the connected file. The
    // CRM's own invoicing stays the system of record - this is a copy out,
    // never a move, and nothing is ever pushed twice.
    const pushInvoices = async (row: IntegrationRow) => {
        setError(null);
        setPushResult(null);
        setPushing(row.provider);
        try {
            const summary = await api.post<{ candidates: number; pushed: number; failed: number }>(
                `/integrations/${row.provider}/push-invoices`, {});
            setPushResult(summary.candidates === 0
                ? "Nothing to push — every issued invoice is already in the books."
                : `Pushed ${summary.pushed} invoice${summary.pushed === 1 ? "" : "s"} to ${row.label}`
                + (summary.failed > 0 ? `; ${summary.failed} failed — see the activity below.` : "."));
            syncLog.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not push invoices");
        } finally {
            setPushing(null);
        }
    };

    return (
        <div className="card">
            <div className="card-head">
                <div>
                    <h2>Integrations</h2>
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                        Connect your accounting software. You sign in on the provider&rsquo;s own
                        page — TrustCare never sees the password, only the connection it grants.
                    </p>
                </div>
            </div>
            <div className="card-body flush">
                {(integrations.data ?? []).map((row) => (
                    <div key={row.provider} className="integration-row">
                        <span className="integration-dot"
                            style={{ background: INTEGRATION_COLOURS[row.provider] ?? "#888" }}
                            aria-hidden="true">
                            {row.label[0]}
                        </span>
                        <strong>{row.label}</strong>
                        {row.status === "connected" ? (
                            <span style={{ marginLeft: "auto", display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                <span className="chip pass">
                                    Connected{row.tenantName ? ` — ${row.tenantName}` : ""}
                                </span>
                                <button className="btn small" disabled={pushing === row.provider}
                                    onClick={() => void pushInvoices(row)}>
                                    {pushing === row.provider ? "Pushing…" : "Push invoices"}
                                </button>
                                <button className="btn ghost small" onClick={() => void disconnect(row)}>
                                    Disconnect
                                </button>
                            </span>
                        ) : (
                            <span style={{ marginLeft: "auto", display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                {waiting === row.provider && <span className="chip info">Waiting for sign-in…</span>}
                                <button className="btn small" onClick={() => void connect(row)}>
                                    Connect
                                </button>
                            </span>
                        )}
                    </div>
                ))}
                {pushResult && (
                    <div style={{ padding: "0.6rem 1rem", fontSize: "0.82rem" }}>{pushResult}</div>
                )}
                {error && <div className="error-banner" style={{ margin: "0.6rem" }}>{error}</div>}
                {(syncLog.data ?? []).length > 0 && (
                    <div style={{ padding: "0.8rem 1rem", borderTop: "1px solid var(--line, #eee)" }}>
                        <strong style={{ fontSize: "0.82rem" }}>Recent sync activity</strong>
                        <ul style={{ listStyle: "none", margin: "0.4rem 0 0", padding: 0, fontSize: "0.78rem" }}>
                            {(syncLog.data ?? []).slice(0, 8).map((entry, idx) => (
                                <li key={idx} style={{ display: "flex", gap: "0.5rem", alignItems: "baseline", padding: "0.15rem 0" }}>
                                    <span className={`chip ${entry.outcome === "pushed" ? "pass"
                                        : entry.outcome === "failed" ? "fail" : "info"}`}>
                                        {entry.outcome}
                                    </span>
                                    <span>{entry.entityLabel ?? entry.provider}</span>
                                    <span style={{ color: "var(--muted)" }}>{entry.detail}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {showSetup && (
                    <div style={{ padding: "0.8rem 1rem", borderTop: "1px solid var(--line, #eee)" }}>
                        <strong>{showSetup.label} needs a one-time app registration first.</strong>
                        <p style={{ fontSize: "0.82rem", marginTop: "0.3rem" }}>{showSetup.instructions}</p>
                        <button className="btn ghost small" onClick={() => setShowSetup(null)}>Close</button>
                    </div>
                )}
            </div>
        </div>
    );
};
